import React, { useEffect, useRef, useState } from 'react';
import "./bounding-box-drawer.css";

const BoundingBoxDrawer = ({ imageSrc, boxes, liveData }) => {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!imageSrc) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = imageSrc;

    let scaledBoxes = [];

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      // Scale the relative polygon coordinates to the actual image dimensions
      scaledBoxes = boxes.map(box => ({
        ...box,
        scaledPolygon: box.polygon.map(p => [p[0] * img.width, p[1] * img.height])
      }));
    };

    // Helper function to check if a point is inside a polygon using the ray-casting algorithm
    const isPointInPolygon = (point, polygon) => {
      const [x, y] = point;
      let isInside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];

        const intersect = ((yi > y) !== (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) isInside = !isInside;
      }
      return isInside;
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;
      let found = false;

      for (const box of scaledBoxes) {
        if (isPointInPolygon([mouseX, mouseY], box.scaledPolygon)) {
          const busName = box.label;
          const busMetrics = liveData[busName];

          setTooltip({
            title: busName,
            x: e.clientX,
            y: e.clientY,
            metrics: busMetrics || null
          });

          found = true;
          break;
        }
      }

      if (!found) setTooltip(null);
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [imageSrc, boxes, liveData]);

  return (
    <div className="sld-image">
      <canvas ref={canvasRef} className="bounding-canvas" />
      {tooltip && (
      <div className="tooltip-box"
        style={{
          position: 'fixed',
          left: `${tooltip.x + 10}px`,
          top: `${tooltip.y + 10}px`,
          border: '1px solid #ccc',
          padding: '8px',
          borderRadius: '4px',
          pointerEvents: 'none',
          zIndex: 9999
        }}>
          <strong>{tooltip.title}</strong>
          {tooltip.metrics ? (
            <div style={{ marginTop: '4px' }}>
              <div>Voltage Magnitude : {tooltip.metrics.Voltage_Mag.toFixed(2)}</div>
              <div>Voltage Angle : {tooltip.metrics.Voltage_Ang.toFixed(2)}</div>
              <div>P (MW) : {tooltip.metrics.P_MW.toFixed(2)}</div>
              <div>Q (MVar) : {tooltip.metrics.Q_MVar.toFixed(2)}</div>
            </div>
          ) : (
            <div>No live data available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default BoundingBoxDrawer;